import { memo, useEffect, useState } from "react"
import { format } from "date-fns"
import { AnimatePresence, motion } from "motion/react"

import type { UpdateThreadBulkArgs } from "@productlane/zero/types"
import { cn } from "@productlane/lib/src/cn"
import { ShortcutSymbols } from "@productlane/lib/src/shortcut-symbols"
import { AlertDialog, Button, Shortcut, SnoozeIcon } from "@productlane/ui"
import { mutators } from "@productlane/zero/mutators"

import type { ThreadSelectionSource } from "./use-select-all-threads-shortcut"
import type { InboxState } from "@/lib/states/global-state"
import { toast } from "@/components/toast"
import { useShortcut } from "@/lib"
import { useEscapeClearsSelection } from "@/lib/hooks/use-escape-clears-selection"
import { useThreadState } from "@/lib/states/global-state"
import { mutateOk, useZero } from "@/lib/zero"
import { resolveInboxListKind } from "../../inbox/helpers/assigned-sub-tab"
import { useInboxAnchorReset } from "../../inbox/inbox-anchor-reset"
import { pickSnoozeTime } from "../components/center-content/snooze-dialog"
import { bulkReopenPrompt } from "./bulk-reopen-prompt"
import {
	useIsThreadsMultiSelectActive,
	useThreadsMultiSelectState,
} from "./threads-multi-select-state"
import {
	useSelectAllThreads,
	useSelectAllThreadsShortcut,
} from "./use-select-all-threads-shortcut"

type AnimationDirection = "up" | "down"

// Each push runs one transaction with an activity row and an Inngest event per thread.
const MAX_IDS_PER_CALL = 500

function chunks<T>(items: T[], size = MAX_IDS_PER_CALL): T[][] {
	const out: T[][] = []
	for (let i = 0; i < items.length; i += size)
		out.push(items.slice(i, i + size))
	return out
}

const variants = {
	enter: (direction: AnimationDirection) => {
		return {
			transform: direction === "up" ? "translateY(-100%)" : "translateY(100%)",
			opacity: 0,
		}
	},
	center: {
		opacity: 1,
		transform: "translateY(0)",
	},
	exit: (direction: AnimationDirection) => {
		return {
			transform: direction === "up" ? "translateY(100%)" : "translateY(-100%)",
			opacity: 0,
		}
	},
}

interface Props {
	threadTab: InboxState
	allThreadIds: Array<string>
	selection?: ThreadSelectionSource
	className?: string
}

const ACTION_HINT_NARROW = "[@container(max-width:26rem)]:hidden"
const ACTION_HINT = cn("ml-1", ACTION_HINT_NARROW)

export const ThreadsMultiSelectPopup = memo(function ThreadsMultiSelectPopup({
	threadTab,
	allThreadIds,
	selection,
	className,
}: Props) {
	const { threadIds, clearThreadIds } = useThreadsMultiSelectState()
	const selectAll = useSelectAllThreads(allThreadIds, selection)
	const [prevThreadIdsLength, setPrevThreadIdsLength] = useState(
		threadIds.length,
	)
	const { isThreadsMultiSelectActive } = useIsThreadsMultiSelectActive()
	const zero = useZero()
	const resetInboxAnchor = useInboxAnchorReset((s) => s.resetInboxAnchor)
	const { assignedSubTab, activeCustomViewId } = useThreadState()

	const listKind = resolveInboxListKind(
		threadTab,
		!!activeCustomViewId,
		assignedSubTab,
	)
	const isDoneList = listKind === "done"
	const isSnoozedList = listKind === "snoozed"

	// Esc clears the selection before the inbox switches tab or leaves the thread.
	useEscapeClearsSelection(isThreadsMultiSelectActive, clearThreadIds)

	// Some animation fun so that when numbers increase the number comes in from the top and when decreasing from the bottom
	useEffect(() => {
		if (threadIds.length !== prevThreadIdsLength) {
			setPrevThreadIdsLength(threadIds.length)
		}
	}, [threadIds.length, prevThreadIdsLength])

	const animationKey = String(threadIds.length)
	const animationDirection =
		threadIds.length > prevThreadIdsLength ? "up" : "down"
	const [isConfirmDeleteOpen, setIsConfirmDeleteOpen] = useState(false)
	const [isConfirmReopenOpen, setIsConfirmReopenOpen] = useState(false)

	const updateBulk = (
		ids: string[],
		change: Omit<UpdateThreadBulkArgs, "ids">,
	) =>
		Promise.all(
			chunks(ids).map((batch) =>
				mutateOk(
					zero.mutate(mutators.threads.updateBulk({ ...change, ids: batch })),
				),
			),
		)

	const deleteHandler = () => {
		setIsConfirmDeleteOpen(true)
	}

	const processedHandler = async () => {
		const ids = threadIds
		resetInboxAnchor()
		await updateBulk(ids, {
			state: "PROCESSED",
			lastStateChangeAtIso: new Date().toISOString(),
		})
		toast.success({
			title: `Marked ${ids.length} thread${ids.length > 1 ? "s" : ""} as done.`,
		})
		//? set timeout is required there since we have new inbox layout and it overlaps with top-bar cmd+e shortcut
		setTimeout(clearThreadIds, 0)
	}
	const reopenHandler = () => {
		setIsConfirmReopenOpen(true)
	}
	const inboxHandler = async () => {
		setIsConfirmReopenOpen(false)
		const ids = threadIds
		resetInboxAnchor()
		await updateBulk(ids, {
			state: "NEW",
			lastStateChangeAtIso: new Date().toISOString(),
		})
		toast.success({
			title: `Moved ${ids.length} thread${ids.length > 1 ? "s" : ""} to Inbox.`,
		})
		setTimeout(clearThreadIds, 0)
	}
	const snoozeBulk = async (value: Date) => {
		const ids = threadIds
		resetInboxAnchor()
		await updateBulk(ids, {
			state: "SNOOZED",
			snoozedUntilIso: value.toISOString(),
			lastStateChangeAtIso: new Date().toISOString(),
		})
		toast.success({
			icon: SnoozeIcon,
			title: `Snoozed ${ids.length} thread${ids.length > 1 ? "s" : ""} until ${format(value, "EEE, d MMM, h:mm a")}.`,
		})
		setTimeout(clearThreadIds, 0)
	}
	const snoozeHandler = () => {
		pickSnoozeTime((value) => void snoozeBulk(value))
	}
	const unsnoozeHandler = async () => {
		const ids = threadIds
		resetInboxAnchor()
		await updateBulk(ids, {
			state: "UNSNOOZED",
			snoozedUntilIso: null,
			lastStateChangeAtIso: new Date().toISOString(),
		})
		toast.success({
			icon: SnoozeIcon,
			title: `Unsnoozed ${ids.length} thread${ids.length > 1 ? "s" : ""}.`,
		})
		setTimeout(clearThreadIds, 0)
	}
	useShortcut(
		"meta+backspace, ctrl+backspace",
		() => {
			deleteHandler()
		},
		{
			enabled: isThreadsMultiSelectActive,
		},
	)
	useShortcut(
		"e",
		(event) => {
			if (event.repeat) return
			if (isDoneList) {
				reopenHandler()
			} else {
				void processedHandler()
			}
		},
		{
			preventDefault: true,
			enableOnContentEditable: true,
			enabled: isThreadsMultiSelectActive,
		},
	)
	useSelectAllThreadsShortcut(selectAll, allThreadIds.length > 0)
	useShortcut(
		"h",
		() => {
			if (isSnoozedList) {
				void unsnoozeHandler()
			} else {
				snoozeHandler()
			}
		},
		{
			preventDefault: true,
			enabled: isThreadsMultiSelectActive,
		},
	)

	return (
		<AnimatePresence>
			{threadIds.length === 0 ? null : (
				<motion.div
					key="action-container"
					className={cn(
						"shrink-0 border-t border-gray-200 dark:border-gray-700/80",
						className,
					)}
					initial={{ opacity: 0 }}
					animate={{ opacity: 1 }}
					exit={{ opacity: 0 }}
				>
					<div className="flex flex-col overflow-hidden bg-white dark:bg-gray-800">
						<div className="flex items-center justify-between border-b border-gray-200 px-3.5 dark:border-gray-700/80">
							<div className="relative flex items-end py-1.5 text-sm text-secondary">
								<AnimatePresence
									mode="popLayout"
									initial={false}
									custom={animationDirection}
								>
									<motion.div
										custom={animationDirection}
										variants={variants}
										initial="enter"
										animate="center"
										exit="exit"
										key={animationKey}
										className="tabular-nums"
									>
										{animationKey}
									</motion.div>
								</AnimatePresence>

								<div className="ml-1 text-xs">selected</div>
							</div>

							<Button
								variant="ghost"
								size="sm"
								className="rounded-none"
								onClick={selectAll}
							>
								All
								<Shortcut className="ml-1">{ShortcutSymbols.CMD}</Shortcut>
								<Shortcut className="ml-1">{ShortcutSymbols.A}</Shortcut>
							</Button>

							<Button
								variant="ghost"
								size="sm"
								className="rounded-none"
								onClick={() => {
									clearThreadIds()
								}}
							>
								Clear
								<Shortcut className="ml-1">{ShortcutSymbols.esc}</Shortcut>
							</Button>
						</div>

						<div className="flex items-center overflow-hidden [container-type:inline-size]">
							<Button
								variant="ghost"
								size="sm"
								className="min-w-0 flex-1 rounded-none"
								onClick={isDoneList ? reopenHandler : processedHandler}
							>
								{isDoneList ? "Move to Inbox" : "Mark done"}
								<Shortcut className={ACTION_HINT}>{ShortcutSymbols.E}</Shortcut>
							</Button>

							<div className="h-5 w-[0.5px] flex-none bg-gray-200 dark:bg-gray-700" />

							{isSnoozedList ? (
								<Button
									variant="ghost"
									size="sm"
									className="min-w-0 flex-1 rounded-none"
									onClick={unsnoozeHandler}
								>
									Unsnooze
									<Shortcut className={ACTION_HINT}>
										{ShortcutSymbols.H}
									</Shortcut>
								</Button>
							) : (
								<Button
									variant="ghost"
									size="sm"
									className="min-w-0 flex-1 rounded-none"
									onClick={snoozeHandler}
								>
									Snooze
									<Shortcut className={ACTION_HINT}>
										{ShortcutSymbols.H}
									</Shortcut>
								</Button>
							)}

							<div className="h-5 w-[0.5px] flex-none bg-gray-200 dark:bg-gray-700" />

							<Button
								variant="ghost"
								size="sm"
								className="min-w-0 flex-1 rounded-none"
								onClick={deleteHandler}
							>
								Delete
								<Shortcut className={ACTION_HINT}>
									{ShortcutSymbols.CMD}
								</Shortcut>
								<Shortcut className={cn("ml-0.5", ACTION_HINT_NARROW)}>
									{ShortcutSymbols.BACKSPACE}
								</Shortcut>
							</Button>

							<AlertDialog
								isOpen={isConfirmReopenOpen}
								onOpenChange={setIsConfirmReopenOpen}
								variant="positive"
								{...bulkReopenPrompt(threadIds.length)}
								onCancel={() => {
									setIsConfirmReopenOpen(false)
								}}
								onConfirm={inboxHandler}
							/>

							<AlertDialog
								isOpen={isConfirmDeleteOpen}
								onOpenChange={setIsConfirmDeleteOpen}
								confirmText="Delete"
								title="Are you sure?"
								description={`Do you really want to delete ${
									threadIds.length
								} thread${threadIds.length > 1 ? "s" : ""}?`}
								onCancel={() => {
									setIsConfirmDeleteOpen(false)
								}}
								onConfirm={async () => {
									setIsConfirmDeleteOpen(false)
									const idsToDelete = [...threadIds]
									const count = idsToDelete.length
									clearThreadIds()
									resetInboxAnchor()
									await Promise.all(
										chunks(idsToDelete).map((ids) =>
											mutateOk(
												zero.mutate(mutators.threads.deleteBulk({ ids })),
												"server",
											),
										),
									)
									toast.success({
										title: `Deleted ${count} thread${count > 1 ? "s" : ""}.`,
									})
								}}
							/>
						</div>
					</div>
				</motion.div>
			)}
		</AnimatePresence>
	)
})
